import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { dealApi, dealQueryKeys } from '@crm/entities/deal';
import { dealsBoardQueryKeys } from '@crm/entities/deals-board';
import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';

import type { TDeal } from '@crm/entities/deal';

interface ReturnDealsToPipelineMutationVariables {
    targetColumnId: TDeal['columnId'];
    targetColumnTitle: string;
}

export const useReturnDealsToPipelineMutation = ({
    dealIds,
}: {
    dealIds: readonly TDeal['id'][];
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: dealQueryKeys.returnToBoard(dealIds),
        mutationFn: ({ targetColumnId }: ReturnDealsToPipelineMutationVariables) => {
            if (dealIds.length === 0 || new Set(dealIds).size !== dealIds.length) {
                throw new Error(t('crm.common.deal.errors.invalidIds'));
            }

            return dealApi.returnDealsToBoard({
                dealIds,
                targetColumnId,
            });
        },
        onSuccess: async (deals, { targetColumnTitle }) => {
            await Promise.all([
                ...dealIds.map((dealId) =>
                    queryClient.invalidateQueries({
                        queryKey: dealQueryKeys.detail(dealId),
                    })
                ),
                queryClient.invalidateQueries({
                    queryKey: dealsBoardQueryKeys.board(),
                }),
                queryClient.invalidateQueries({
                    queryKey: dealQueryKeys.lists.all(),
                }),
            ]);

            const title =
                deals.length === 1
                    ? t('crm.returnDealsToPipeline.notifications.success.single', {
                          pipeline: targetColumnTitle,
                          interpolation: { escapeValue: false },
                      })
                    : t('crm.returnDealsToPipeline.notifications.success.batch', {
                          count: deals.length,
                          pipeline: targetColumnTitle,
                          interpolation: { escapeValue: false },
                      });

            toastManager.onSuccess({ title });
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(
                    error,
                    t('crm.returnDealsToPipeline.errors.submit')
                ),
            });
        },
    });
};
